import { getActors, getFirstAction } from "@mirage/service-feed/util/helpers";
import { Trans } from "@mirage/translations";
import {
  ActivityLabelSubject,
  ActivityLabelSubjectOverflow,
  getActorNames,
} from "../ActivityLabel/ActivityLabelSubject";

import type { dash_feed, users } from "@dropbox/api-v2-client";
import type { SearchResult } from "@mirage/shared/search/search-result";

type DocumentActivityLabelProps = {
  activity: dash_feed.FeedItem;
  document: SearchResult;
  currentAccount?: users.FullAccount;
};

type EditDocumentActivityLabelProps = {} & DocumentActivityLabelProps;
type ShareDocumentActivityLabelProps = {} & DocumentActivityLabelProps;

export const DocumentActivityLabel = ({
  activity,
  document,
  currentAccount,
}: DocumentActivityLabelProps) => {
  const action = getFirstAction(activity);
  switch (action?.[".tag"]) {
    case "edit":
      return (
        <EditDocumentActivityLabel
          currentAccount={currentAccount}
          activity={activity}
          document={document}
        />
      );
    case "share":
      return (
        <ShareDocumentActivityLabel
          currentAccount={currentAccount}
          activity={activity}
          document={document}
        />
      );
    default:
      return null;
  }
};

export const EditDocumentActivityLabel = ({
  activity,
}: EditDocumentActivityLabelProps) => {
  const actors = getActors(activity);
  const { actorNamesToDisplay, extraActorsToDisplay, actorsToDisplay } =
    getActorNames(actors);

  if (actorNamesToDisplay.length === 0) {
    return (
      <Trans
        i18nKey={"activity_feed_expanded_label_edit_document_no_actor"}
        components={{ subject: <ActivityLabelSubject /> }}
      />
    );
  } else if (actorNamesToDisplay.length === 1) {
    return (
      <Trans
        i18nKey={"activity_feed_expanded_label_edit_document_one_actor"}
        values={{
          actorName1: actorNamesToDisplay[0],
        }}
        components={{
          subject: (
            <ActivityLabelSubject actorEmail={actorsToDisplay[0].email} />
          ),
        }}
      />
    );
  } else if (actorNamesToDisplay.length === 2) {
    return (
      <Trans
        i18nKey={"activity_feed_expanded_label_edit_document_two_actors"}
        values={{
          actorName1: actorNamesToDisplay[0],
          actorName2: actorNamesToDisplay[1],
          count: extraActorsToDisplay.length,
        }}
        components={{
          subject1: (
            <ActivityLabelSubject actorEmail={actorsToDisplay[0].email} />
          ),
          subject2: (
            <ActivityLabelSubject actorEmail={actorsToDisplay[1].email} />
          ),
          subjectOverflow: (
            <ActivityLabelSubjectOverflow extraActors={extraActorsToDisplay} />
          ),
        }}
      />
    );
  }
  return (
    <Trans
      i18nKey={"activity_feed_expanded_label_edit_document_three_actors"}
      values={{
        actorName1: actorNamesToDisplay[0],
        actorName2: actorNamesToDisplay[1],
        actorName3: actorNamesToDisplay[2],
      }}
      components={{
        subject1: (
          <ActivityLabelSubject actorEmail={actorsToDisplay[0].email} />
        ),
        subject2: (
          <ActivityLabelSubject actorEmail={actorsToDisplay[1].email} />
        ),
        subject3: (
          <ActivityLabelSubject actorEmail={actorsToDisplay[2].email} />
        ),
      }}
    />
  );
};

export const ShareDocumentActivityLabel = ({
  activity,
}: ShareDocumentActivityLabelProps) => {
  const actors = getActors(activity);
  const { actorNamesToDisplay } = getActorNames(actors);

  if (actorNamesToDisplay.length === 0) {
    return (
      <Trans
        i18nKey={"activity_feed_expanded_label_share_document_no_actor"}
        components={{ subject: <ActivityLabelSubject /> }}
      />
    );
  }

  return (
    <Trans
      i18nKey={"activity_feed_expanded_label_share_document_one_actor"}
      values={{
        actorName1: actorNamesToDisplay[0],
      }}
      components={{ subject: <ActivityLabelSubject /> }}
    />
  );
};
